import React, { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { Sun, Moon, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';

const OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'dark', label: 'Dark', icon: Moon },
];

/** Cross-fade colours for one beat around a theme change (skipped for reduced motion). */
function withThemeTransition(apply) {
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (reduce) { apply(); return; }
  const root = document.documentElement;
  root.classList.add('theme-transition');
  apply();
  window.setTimeout(() => root.classList.remove('theme-transition'), 320);
}

/**
 * Segmented Light / System / Dark control. Responds on press, remembers the choice
 * (next-themes stores it), and follows the OS when set to System.
 */
export default function ThemeToggle({ className, compact = false }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const current = mounted ? theme : 'system';

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={cn(
        'inline-flex items-center rounded-full border border-border bg-muted p-0.5',
        className
      )}
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = current === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            title={`${label} theme`}
            onPointerDown={() => !active && withThemeTransition(() => setTheme(value))}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !active) {
                e.preventDefault();
                withThemeTransition(() => setTheme(value));
              }
            }}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded-full text-xs font-medium',
              'transition-colors duration-150',
              compact ? 'h-7 w-7' : 'h-7 px-2.5',
              active
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {!compact && <span className="hidden xl:inline">{label}</span>}
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
